import { useConfirmationDialog } from 'components/ConfirmationDialog'
import { AuthContext } from 'modules/Auth'
import { LoadPage } from 'components/LoadPage'
import { NotFoundPage } from 'components/NotFoundPage'
import {
  Client,
  ClientStatus,
  Currency,
  Installment,
  Order,
  OrderStatus,
  Plan,
  Role
} from 'models'
import { subscriptionBaseSteps } from 'modules/Orders/helpers/constants'
import {
  buildStatusDatesForStepsPanel,
  updateSteps
} from 'modules/Orders/helpers/utils'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { show } from '../services/read'
import { toast } from 'react-toastify'
import { ProtectedSection } from 'components/ProtectedSection'
import { CancelButton } from 'components/FormInputs/Button'
import { StepPanel } from 'modules/Orders/components/StepPanel'
import { ReviewTable } from 'modules/Subscriptions/components/ReviewTable'
import { activate, approve, cancel, markAsPending } from '../services/update'
import {
  updateInstallmentDates,
  updateOrderDates
} from 'modules/Orders/services'
import { isSameDay, setTodayTimeFrom } from 'utils/datetime'
import {
  determineCancelationMinDate,
  getOrderStatusAndDate,
  orderStatusSequence,
  orderStatusToDateColumn
} from 'utils/order-status'
import { InstallmentTable } from 'modules/Orders/components/Installments'
import { AssignOwnerModal } from 'modules/Orders/components/AssignOwnerModal'
import { show as showClient } from 'modules/Clients/services/read'
import { date } from 'utils'
import { getCurrentClientStatus } from 'modules/Clients/helpers/utils'

export function Show() {
  const { subscriptionId } = useParams() as unknown as {
    subscriptionId: string
  }

  const navigate = useNavigate()

  const { user } = useContext(AuthContext)
  const userRole = user?.relationships?.role.attributes.name as Role

  const [isLoading, setIsLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [steps, setSteps] = useState(
    subscriptionBaseSteps.map((step) => Object.assign({}, step))
  )

  const [subscription, setSubscription] = useState<Order>()
  const [isAssignOwnerOpen, setIsAssignOwnerOpen] = useState(false)

  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState(
    OrderStatus.pending
  )

  const [statusDates, setStatusDates] = useState<Record<string, any>>({})

  const [canceledAt, setCanceledAt] = useState(new Date())
  const canceledAtRef = useRef(canceledAt)

  const [approvedAt, setApprovedAt] = useState(new Date())
  const approvedAtRef = useRef(approvedAt)

  const [activatedAt, setActivatedAt] = useState(new Date())
  const activatedAtRef = useRef(activatedAt)

  const [pendingAt, setPendingAt] = useState(new Date())
  const pendingAtRef = useRef(pendingAt)

  const orderDateEditRef = useRef(new Date())
  const installmentDateEditRef = useRef(new Date())

  const [installments, setInstallments] = useState<Installment[]>([])

  useEffect(() => {
    canceledAtRef.current = canceledAt
    approvedAtRef.current = approvedAt
    activatedAtRef.current = activatedAt
    pendingAtRef.current = pendingAt
  }, [canceledAt, approvedAt, activatedAt, pendingAt])

  const [plan, setPlan] = useState<Partial<Plan>>()
  const [client, setClient] = useState<Partial<Client>>()

  const [clientStatus, setClientStatus] = useState<ClientStatus>()

  const [showConfirmationDialog] = useConfirmationDialog()

  const updateSubscriptionStatus = useCallback(
    (currentStatus: OrderStatus) => {
      setCurrentSubscriptionStatus(currentStatus)

      if (currentStatus === 'canceled') {
        setSteps(subscriptionBaseSteps)
      } else {
        setSteps([...updateSteps(steps, currentStatus)])
      }

      return currentStatus
    },
    [steps]
  )

  useEffect(() => {
    if (!subscriptionId) return

    const fetchSubscription = async () => {
      try {
        const subscription = await show(subscriptionId, {
          includes: {
            client: ['id'],
            plan: ['id', 'name', 'nature', 'logistics', 'currency', 'amount'],
            coupon: ['*'],
            order_installments: ['*'],
            product: ['*'],
            staff_member: ['id', 'fullname']
          }
        })

        if (subscription.relationships?.order_installments) {
          setInstallments(
            subscription.relationships.order_installments
              .map(({ id, attributes }: any) => ({
                id,
                type: attributes.type,
                amount: attributes.amount,
                due_at: attributes.due_at,
                upfront: attributes.upfront,
                eligible: attributes.eligible
              }))
              .sort(
                (a: Installment, b: Installment) =>
                  new Date(a.due_at).getTime() - new Date(b.due_at).getTime()
              )
          )
        }

        setSubscription(subscription as Order)

        setPlan({
          id: subscription?.relationships?.plan?.id,
          ...(subscription?.relationships?.plan?.attributes ?? {}),
          relationships: { product: subscription?.relationships?.product }
        })

        const client = await showClient(
          subscription?.relationships?.client?.id,
          {
            includes: { statuses: ['*'] }
          }
        )

        setClient({ id: client.id, ...client.attributes })
        setClientStatus(
          getCurrentClientStatus(client.relationships?.statuses)?.attributes
            .status
        )

        setStatusDates(buildStatusDatesForStepsPanel(subscription))

        const subscriptionStatus = subscription.current_status
        updateSubscriptionStatus(subscriptionStatus as OrderStatus)

        setIsLoading(false)
      } catch (err: any) {
        navigate('/subscriptions')
        toast.error(
          err.suggestedMessage ?? 'Falha ao buscar os dados da assinatura'
        )
      }
    }
    fetchSubscription()
  }, [subscriptionId, refreshTrigger])

  const onCancelConfirmation = useCallback(async () => {
    try {
      const cancelDate = canceledAtRef.current
      if (isSameDay(new Date(), cancelDate)) {
        await cancel(subscriptionId)
      } else {
        await cancel(subscriptionId, cancelDate)
      }
      const data = await show(subscriptionId, {
        attributes: ['id', 'current_status']
      })
      updateSubscriptionStatus(data?.current_status as OrderStatus)
      toast.success('Assinatura cancelada com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível cancelar a assinatura!'
      )
    }
  }, [subscriptionId, updateSubscriptionStatus])

  const onCancelRequest = useCallback(() => {
    showAndUpdateConfirmationDialog(
      cancelOrderModalAttributes,
      canceledAt,
      true
    )
  }, [
    currentSubscriptionStatus,
    onCancelConfirmation,
    plan?.fiscal_data,
    showConfirmationDialog
  ])

  const onApprovalConfirmation = useCallback(async () => {
    const approvedAt = approvedAtRef.current
    try {
      await approve(subscriptionId, approvedAt)
      const data = await show(subscriptionId, {
        attributes: [
          'id',
          'current_status',
          'ordered_at',
          'approved_at',
          'activated_at'
        ]
      })

      setStatusDates(buildStatusDatesForStepsPanel(data))

      // Garante que a data de ativação padrão seja sempre posterior à data de aprovação
      if (data.approved_at) {
        const newApprovedAt = new Date(data.approved_at)
        setActivatedAt((prev) =>
          prev > newApprovedAt ? prev : new Date(newApprovedAt.getTime() + 1000)
        )
      }

      updateSubscriptionStatus(data?.current_status as OrderStatus)
      toast.success('Assinatura aprovada com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível aprovar a assinatura!'
      )
    }
  }, [subscriptionId, updateSubscriptionStatus])

  const onApprovalRequest = useCallback(() => {
    showAndUpdateConfirmationDialog(
      approveOrderModalAttributes,
      approvedAt,
      true,
      'status_update'
    )
  }, [onApprovalConfirmation, showConfirmationDialog, approvedAt])

  const onActivationConfirmation = useCallback(async () => {
    const activatedAt = activatedAtRef.current
    try {
      await activate(subscriptionId, activatedAt)
      const data = await show(subscriptionId, {
        attributes: [
          'id',
          'current_status',
          'ordered_at',
          'approved_at',
          'activated_at'
        ]
      })

      setStatusDates(buildStatusDatesForStepsPanel(data))

      updateSubscriptionStatus(data?.current_status as OrderStatus)
      toast.success('Assinatura ativada com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível ativar a assinatura!'
      )
    }
  }, [subscriptionId, updateSubscriptionStatus])

  const onActivateRequest = useCallback(() => {
    showAndUpdateConfirmationDialog(
      activateOrderModalAttributes,
      activatedAt,
      true,
      'status_update'
    )
  }, [onActivationConfirmation, showConfirmationDialog, activatedAt])

  const onMarkAsPendingConfirmation = useCallback(async () => {
    const pendingAt = pendingAtRef.current
    try {
      await markAsPending(subscriptionId, pendingAt)
      const data = await show(subscriptionId, {
        attributes: [
          'id',
          'current_status',
          'ordered_at',
          'approved_at',
          'activated_at'
        ]
      })

      setStatusDates(buildStatusDatesForStepsPanel(data))

      updateSubscriptionStatus(data?.current_status as OrderStatus)
      toast.success('Assinatura marcada como pendente com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ??
          'Não foi possível marcar a assinatura como pendente!'
      )
    }
  }, [subscriptionId, updateSubscriptionStatus])

  const onMarkAsPendingRequest = useCallback(() => {
    showAndUpdateConfirmationDialog(
      markAsPendingOrderModalAttributes,
      pendingAt,
      false,
      'status_update'
    )
  }, [onMarkAsPendingConfirmation, showConfirmationDialog, pendingAt])

  const onCanceledAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setCanceledAt(d)
    return d
  }, [])

  const handleStatusChangeRequest = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.pending:
        onMarkAsPendingRequest()
        break
      case OrderStatus.approved:
        onApprovalRequest()
        break
      case OrderStatus.delivered:
        onActivateRequest()
        break
      default:
        console.warn('Status não tratado:', status)
    }
  }

  const onApprovedAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setApprovedAt(d)
    return d
  }, [])

  const onActivatedAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setActivatedAt(d)
    return d
  }, [])

  const onPendingAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setPendingAt(d)
    return d
  }, [])

  const onOrderDateEditRequest = useCallback(
    (stepLabel: string, currentDate: Date) => {
      const idx = orderStatusSequence.indexOf(stepLabel)
      // Primeiro status da sequência (pending) não tem status anterior — sem limite inferior
      let minDate: Date | undefined =
        idx === 0 ? undefined : new Date(subscription?.ordered_at as string)
      let maxDate: Date = new Date()

      if (idx !== -1) {
        for (let i = idx - 1; i >= 0; i--) {
          const d = statusDates[orderStatusSequence[i]]
          if (d) {
            minDate = new Date(d)
            break
          }
        }
        for (let i = idx + 1; i < orderStatusSequence.length; i++) {
          const d = statusDates[orderStatusSequence[i]]
          if (d) {
            maxDate = new Date(d)
            break
          }
        }
      }

      orderDateEditRef.current = currentDate
      showConfirmationDialog({
        title: 'Editar data',
        message: 'Alterar a data deste status da assinatura?',
        datePicker: true,
        datePickerAttributes: { minDate: minDate as Date, maxDate },
        date: currentDate,
        onDateChange: (val) => {
          const d = val instanceof Date ? val : new Date(val)
          orderDateEditRef.current = d
          return d
        },
        onConfirm: async () => {
          try {
            await updateOrderDates(subscription?.id as number, {
              [orderStatusToDateColumn[stepLabel]]:
                orderDateEditRef.current.toISOString()
            })
            setRefreshTrigger((t) => t + 1)
            toast.success('Data atualizada com sucesso!')
          } catch (err: any) {
            toast.error(
              err.suggestedMessage ?? 'Não foi possível atualizar a data'
            )
          }
        }
      })
    },
    [subscription, statusDates, showConfirmationDialog]
  )

  const onInstallmentDateEditRequest = useCallback(
    (installmentId: string, currentDate: Date, index: number) => {
      const installment = installments[index]
      const billingDay = client?.billing_cycle_day ?? 1

      const snapToBillingDay = (d: Date): Date => {
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
        const snapped = new Date(d)
        snapped.setDate(Math.min(billingDay, lastDay))
        return snapped
      }

      const minDate = new Date(subscription?.ordered_at as string)
      const maxDate = new Date(minDate.getFullYear() + 10, 11, 31)

      // Disable: days already occupied by other installments (same month/year),
      // for non-upfront installments any day other than billing_cycle_day,
      // and dates that would break chronological order with adjacent installments.
      const filterDate = (date: Date): boolean => {
        if (!installment.upfront && date.getDate() !== billingDay) return false

        const hasSameMonthYear = installments.some((inst, i) => {
          if (i === index || !inst?.due_at) return false
          const other = new Date(inst.due_at)
          return (
            date.getMonth() === other.getMonth() &&
            date.getFullYear() === other.getFullYear()
          )
        })
        if (hasSameMonthYear) return false

        const toDay = (d: Date) =>
          new Date(d.getFullYear(), d.getMonth(), d.getDate())
        const prevDates = installments
          .slice(0, index)
          .filter((i) => i?.due_at)
          .map((i) => toDay(new Date(i.due_at)))
        const nextDates = installments
          .slice(index + 1)
          .filter((i) => i?.due_at)
          .map((i) => toDay(new Date(i.due_at)))
        const latestPrev =
          prevDates.length > 0
            ? new Date(Math.max(...prevDates.map((d) => d.getTime())))
            : null
        const earliestNext =
          nextDates.length > 0
            ? new Date(Math.min(...nextDates.map((d) => d.getTime())))
            : null

        if (latestPrev && toDay(date) <= latestPrev) return false
        if (earliestNext && toDay(date) >= earliestNext) return false

        return true
      }

      const initialDate = installment.upfront
        ? currentDate
        : snapToBillingDay(currentDate)
      installmentDateEditRef.current = initialDate

      showConfirmationDialog({
        title: 'Editar vencimento',
        message: 'Alterar a data de vencimento desta parcela?',
        datePicker: true,
        datePickerAttributes: { minDate, maxDate, filterDate },
        date: initialDate,
        onDateChange: (val) => {
          const next = val instanceof Date ? val : new Date(val)
          const snapped = installment.upfront ? next : snapToBillingDay(next)
          installmentDateEditRef.current = snapped
          return snapped
        },
        onConfirm: async () => {
          try {
            await updateInstallmentDates([
              {
                id: installmentId,
                due_at: installmentDateEditRef.current.toISOString()
              }
            ])
            setRefreshTrigger((t) => t + 1)
            toast.success('Vencimento atualizado com sucesso!')
          } catch (err: any) {
            toast.error(
              err.suggestedMessage ?? 'Não foi possível atualizar o vencimento'
            )
          }
        }
      })
    },
    [subscription, installments, client, showConfirmationDialog]
  )

  function showAndUpdateConfirmationDialog(
    attributes: Record<string, any>,
    date: Date | string,
    datePicker: boolean,
    operationType?: string
  ) {
    const orderedAt = new Date(subscription?.ordered_at as string)
    const deliveryDate = subscription?.activated_at
      ? new Date(subscription.activated_at)
      : undefined
    const now = new Date()

    let minDate
    const maxDate = now

    if (operationType && operationType === 'status_update') {
      const validDates = Object.values(statusDates).filter(
        (d): d is string => !!d
      )

      minDate = validDates.length
        ? new Date(
            Math.max(
              ...validDates.map((dateStr) => new Date(dateStr).getTime())
            )
          )
        : new Date(subscription?.ordered_at ?? new Date())
    } else {
      minDate = determineCancelationMinDate(deliveryDate, orderedAt as Date)
    }

    showConfirmationDialog({
      title: attributes.title,
      message: attributes.message,
      onConfirm: attributes.onConfirm,
      datePicker,
      datePickerAttributes: {
        minDate: minDate as Date,
        maxDate: maxDate as Date
      },
      date: new Date(date),
      onDateChange: attributes.onDateChange
    })
  }

  const cancelOrderModalAttributes = {
    title: 'Cancelar pedido',
    message: 'Tem certeza que deseja cancelar este pedido?',
    onConfirm: onCancelConfirmation,
    onDateChange: onCanceledAtChange
  }

  const approveOrderModalAttributes = {
    title: 'Aprovar pedido',
    message: 'Tem certeza que deseja aprovar este pedido?',
    onConfirm: onApprovalConfirmation,
    onDateChange: onApprovedAtChange
  }

  const activateOrderModalAttributes = {
    title: 'Ativar assinatura',
    message: 'Tem certeza que deseja ativar esta assinatura?',
    onConfirm: onActivationConfirmation,
    onDateChange: onActivatedAtChange
  }

  const markAsPendingOrderModalAttributes = {
    title: 'Marcar assinatura como pendente',
    message: 'Tem certeza que deseja marcar esta assinatura como pendente?',
    onConfirm: onMarkAsPendingConfirmation,
    onDateChange: onPendingAtChange
  }

  useEffect(() => {
    setApprovedAt(
      setTodayTimeFrom(
        new Date(
          getOrderStatusAndDate({
            id: subscription?.id as number,
            type: 'orders',
            attributes: { ...subscription }
          }).date!
        )
      )
    )
  }, [subscription])

  return isLoading ? (
    <LoadPage />
  ) : !subscription ? (
    <NotFoundPage />
  ) : (
    <div className="overflow-hidden px-4">
      <div className="flex flex-row justify-between items-center py-4 m-4 animate-fade-in-down">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Assinatura: {subscription.token}{' '}
          {currentSubscriptionStatus === 'canceled' && (
            <span className="px-2 mx-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              {`Cancelada em ${date(subscription.closed_at as Date)}`}
            </span>
          )}
        </h3>
      </div>

      <AssignOwnerModal
        open={isAssignOwnerOpen}
        setOpen={setIsAssignOwnerOpen}
        orderId={subscription.id}
        hasOwner={subscription.relationships?.staff_member != null}
        onAssigned={(staffMember) =>
          setSubscription((prev) =>
            prev == null
              ? prev
              : {
                  ...prev,
                  relationships: {
                    ...prev.relationships,
                    staff_member: staffMember
                  }
                }
          )
        }
      />

      <StepPanel
        steps={steps}
        statusDates={statusDates}
        onStatusChangeRequest={handleStatusChangeRequest}
        onDateEditRequest={
          [Role.ROOT, Role.MANAGER, Role.FINANCE].includes(userRole)
            ? onOrderDateEditRequest
            : undefined
        }
        clientStatus={clientStatus}
        planType={plan?.relationships?.product?.attributes?.type}
      />

      <div className="flex">
        <div className="w-full">
          <ReviewTable
            order={subscription}
            client={client}
            plan={plan}
            onAssignOwnerRequest={
              [Role.ROOT, Role.MANAGER].includes(userRole)
                ? () => setIsAssignOwnerOpen(true)
                : undefined
            }
          />
        </div>

        <ProtectedSection roles={[Role.MANAGER, Role.FINANCE, Role.SALES]}>
          {installments.length > 0 && (
            <div className="mx-4 my-8 w-1/2">
              <InstallmentTable
                type={'Taxa de adesão'}
                currency={plan?.currency ?? Currency.brl}
                installments={installments}
                onDateEditRequest={
                  [Role.ROOT, Role.MANAGER, Role.FINANCE].includes(userRole)
                    ? onInstallmentDateEditRequest
                    : undefined
                }
              />
            </div>
          )}
        </ProtectedSection>
      </div>

      {currentSubscriptionStatus !== OrderStatus.canceled && (
        <div className="py-6 gap-x-2 flex flex-row justify-between animate-fade-in-down">
          <ProtectedSection roles={[Role.MANAGER, Role.FINANCE]}>
            <div className="ml-3 text-left text-md">
              <label className="font-medium text-gray-700">
                Cancelar assinatura
              </label>

              <div className="">
                <CancelButton
                  label="Cancelar"
                  onClick={onCancelRequest}
                ></CancelButton>
              </div>
            </div>
          </ProtectedSection>
        </div>
      )}
    </div>
  )
}
